// @ts-nocheck
// Backup & Recovery Framework — Fase 15.3 (architecture only, no real external backups)
export type BackupType   = 'full' | 'incremental' | 'snapshot'
export type BackupStatus = 'pending' | 'running' | 'completed' | 'failed' | 'stale'

export interface BackupSnapshot {
  id: string; type: BackupType; status: BackupStatus; createdAt: string; completedAt: string | null
  sizeBytes: number | null; checksum: string | null; tables: string[]; companyId: string | null
  note: string | null; restorable: boolean
}
export interface RecoveryCheckpoint {
  id: string; snapshotId: string; createdAt: string; environment: string
  appVersion: string; dbVersion: string; validated: boolean; validatedAt: string | null
}
export interface BackupStatusReport {
  timestamp: string; totalSnapshots: number; lastBackupAt: string | null
  lastBackupStatus: BackupStatus | null; hoursSinceBackup: number | null
  checkpoints: number; restorable: boolean; staleThresholdHours: number; isStale: boolean
  recommendations: string[]
}

const STALE_H = 24
let _snapshots:   BackupSnapshot[]     = []
let _checkpoints: RecoveryCheckpoint[] = []

function genId(): string { return `bkp_${Date.now()}_${Math.random().toString(36).slice(2,8)}` }

export function registerSnapshot(snap: Omit<BackupSnapshot, 'id' | 'createdAt'>): BackupSnapshot {
  const full: BackupSnapshot = { ...snap, id: genId(), createdAt: new Date().toISOString() }
  _snapshots.push(full); return full
}
export function updateSnapshotStatus(id: string, status: BackupStatus, extra?: Partial<BackupSnapshot>): void {
  const s = _snapshots.find(s => s.id === id); if (!s) return
  Object.assign(s, { status, ...extra })
  if (status === 'completed') s.completedAt = new Date().toISOString()
}
export function registerCheckpoint(cp: Omit<RecoveryCheckpoint, 'id' | 'createdAt'>): RecoveryCheckpoint {
  const full: RecoveryCheckpoint = { ...cp, id: `cp_${Date.now()}`, createdAt: new Date().toISOString() }
  _checkpoints.push(full); return full
}
export function validateCheckpoint(id: string): void {
  const cp = _checkpoints.find(c => c.id === id)
  if (cp) { cp.validated = true; cp.validatedAt = new Date().toISOString() }
}
export function getSnapshots(): BackupSnapshot[]       { return [..._snapshots] }
export function getCheckpoints(): RecoveryCheckpoint[] { return [..._checkpoints] }
export function getLastRestorableSnapshot(): BackupSnapshot | null {
  return [..._snapshots].reverse().find(s => s.restorable && s.status === 'completed') ?? null
}
export function buildBackupStatusReport(): BackupStatusReport {
  const last = getLastRestorableSnapshot()
  const hoursSince = last ? (Date.now() - new Date(last.completedAt ?? last.createdAt).getTime()) / 3_600_000 : null
  const isStale    = hoursSince === null || hoursSince > STALE_H
  const recs: string[] = []
  if (!last)    recs.push('No restorable backup — register initial backup')
  if (isStale)  recs.push(`Backup stale (${hoursSince?.toFixed(1) ?? 'unknown'}h) — schedule backup`)
  if (_checkpoints.filter(c => c.validated).length === 0) recs.push('No validated checkpoints')
  return {
    timestamp: new Date().toISOString(), totalSnapshots: _snapshots.length,
    lastBackupAt: last?.completedAt ?? last?.createdAt ?? null, lastBackupStatus: last?.status ?? null,
    hoursSinceBackup: hoursSince ? Math.round(hoursSince * 10) / 10 : null,
    checkpoints: _checkpoints.length, restorable: !!last,
    staleThresholdHours: STALE_H, isStale, recommendations: recs,
  }
}
export function resetBackupOrchestrator(): void { _snapshots = []; _checkpoints = [] }
